/**
 * Backend error codes sent via WebSocket
 */
export enum ErrorCode {
  // Session errors
  DuplicateSession = 'duplicate_session',
  SessionExpired = 'session_expired',
  SessionNotFound = 'session_not_found',

  // Authentication errors
  Unauthorized = 'unauthorized',
  Forbidden = 'forbidden',
  InvalidToken = 'invalid_token',

  // User status errors
  Banned = 'banned',
  Suspended = 'suspended',

  // Rate limiting
  RateLimitExceeded = 'rate_limit_exceeded',
  TooManyRequests = 'too_many_requests',

  // Connection errors
  ConnectionTimeout = 'connection_timeout',
  ServerError = 'server_error',

  // Validation errors
  InvalidRequest = 'invalid_request',
  MissingParameter = 'missing_parameter',

  // Frontend/Client errors
  NoLocalStream = 'no_local_stream',
  MediaPermissionDenied = 'media_permission_denied',
  MediaDeviceError = 'media_device_error',
  RTCConnectionFailed = 'rtc_connection_failed',
  WebSocketDisconnected = 'websocket_disconnected',
  BrowserNotSupported = 'browser_not_supported',
  MobileNotSupported = 'mobile_not_supported',
  MultipleSessionsDetected = 'multiple_sessions_detected',

  // RTC
  LocalDescriptionFailed1 = 'local_description_failed_1',
  LocalDescriptionFailed2 = 'local_description_failed_2',

  // Other
  Unknown = 'unknown'
}

export enum ErrorType {
  Rtc = 'rtc',
  WebSocket = 'websocket',
  Auth = 'auth',
  Client = 'client',
  Unknown = 'unknown'
}

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  Critical = 'critical',  // Requires immediate action, blocks usage
  Warning = 'warning',    // User should be aware but can continue
  Info = 'info'          // Informational only
}

/**
 * Standard error interface
 */
export interface AppError {
  code: ErrorCode;
  details?: Record<string, string>;
}

/**
 * Configuration for how to handle each error code
 */
export interface ErrorConfig {
  severity: ErrorSeverity;
  shouldDisplay: boolean;
  retryable: boolean;
  errorType: ErrorType;
  title?: string;          // User-friendly title (defaults to formatted error code)
  message?: string;
  autoHide?: boolean;      // Auto-dismiss after timeout
  autoHideDelay?: number;  // Milliseconds before auto-dismiss
}

/**
 * Map error codes to their handling configuration
 */
export const ERROR_CONFIG: Record<ErrorCode, ErrorConfig> = {
  // RTC
  [ErrorCode.LocalDescriptionFailed1]: {
    severity: ErrorSeverity.Critical,
    shouldDisplay: false,
    retryable: false,
    message: 'Local Description(Offer) not set when having a remote answer.',
    errorType: ErrorType.Rtc
  },
  [ErrorCode.LocalDescriptionFailed2]: {
    severity: ErrorSeverity.Critical,
    shouldDisplay: false,
    retryable: false,
    message: 'Local Description(Answer) not set when having a remote offer.',
    errorType: ErrorType.Rtc
  },
  [ErrorCode.DuplicateSession]: {
    severity: ErrorSeverity.Critical,
    shouldDisplay: true,
    retryable: false,
    errorType: ErrorType.Auth
  },
  [ErrorCode.SessionExpired]: {
    severity: ErrorSeverity.Warning,
    shouldDisplay: true,
    retryable: true,
    errorType: ErrorType.Auth
  },
  [ErrorCode.SessionNotFound]: {
    severity: ErrorSeverity.Warning,
    shouldDisplay: false,
    retryable: true,
    errorType: ErrorType.Auth
  },
  [ErrorCode.Unauthorized]: {
    severity: ErrorSeverity.Critical,
    shouldDisplay: true,
    retryable: false,
    errorType: ErrorType.Auth
  },
  [ErrorCode.Forbidden]: {
    severity: ErrorSeverity.Critical,
    shouldDisplay: true,
    retryable: false,
    errorType: ErrorType.Auth
  },
  [ErrorCode.InvalidToken]: {
    severity: ErrorSeverity.Critical,
    shouldDisplay: false,
    retryable: false,
    errorType: ErrorType.Auth
  },
  [ErrorCode.Banned]: {
    severity: ErrorSeverity.Critical,
    shouldDisplay: true,
    retryable: false,
    errorType: ErrorType.Auth
  },
  [ErrorCode.Suspended]: {
    severity: ErrorSeverity.Critical,
    shouldDisplay: true,
    retryable: false,
    errorType: ErrorType.Auth
  },
  [ErrorCode.RateLimitExceeded]: {
    severity: ErrorSeverity.Warning,
    shouldDisplay: true,
    retryable: true,
    autoHide: true,
    autoHideDelay: 5000,
    errorType: ErrorType.Auth
  },
  [ErrorCode.TooManyRequests]: {
    severity: ErrorSeverity.Warning,
    shouldDisplay: true,
    retryable: true,
    autoHide: true,
    autoHideDelay: 5000,
    errorType: ErrorType.Auth
  },
  [ErrorCode.ConnectionTimeout]: {
    severity: ErrorSeverity.Warning,
    shouldDisplay: true,
    retryable: true,
    errorType: ErrorType.Auth
  },
  [ErrorCode.ServerError]: {
    severity: ErrorSeverity.Critical,
    shouldDisplay: true,
    retryable: true,
    errorType: ErrorType.Auth
  },
  [ErrorCode.InvalidRequest]: {
    severity: ErrorSeverity.Warning,
    shouldDisplay: true,
    retryable: false,
    errorType: ErrorType.Auth
  },
  [ErrorCode.MissingParameter]: {
    severity: ErrorSeverity.Warning,
    shouldDisplay: true,
    retryable: false,
    errorType: ErrorType.Auth
  },
  [ErrorCode.NoLocalStream]: {
    severity: ErrorSeverity.Critical,
    message: 'No local stream available',
    shouldDisplay: true,
    retryable: true,
    errorType: ErrorType.Client
  },
  [ErrorCode.MediaPermissionDenied]: {
    severity: ErrorSeverity.Critical,
    message: 'To connect with people, Blended needs access to your camera and microphone. Please allow access in your browser settings and try again.',
    shouldDisplay: true,
    retryable: true,
    errorType: ErrorType.Client
  },
  [ErrorCode.MediaDeviceError]: {
    severity: ErrorSeverity.Critical,
    shouldDisplay: true,
    retryable: true,
    errorType: ErrorType.Client
  },
  [ErrorCode.RTCConnectionFailed]: {
    severity: ErrorSeverity.Critical,
    shouldDisplay: true,
    errorType: ErrorType.Rtc,
    title: 'Connection Failed',
    message: 'Unable to establish a video connection. Please check your network and try again.',
    retryable: true
  },
  [ErrorCode.WebSocketDisconnected]: {
    severity: ErrorSeverity.Warning,
    shouldDisplay: true,
    retryable: false, // Handled by NetworkErrorOverlay
    errorType: ErrorType.WebSocket
  },
  [ErrorCode.BrowserNotSupported]: {
    severity: ErrorSeverity.Critical,
    shouldDisplay: true,
    retryable: false,
    message: 'Your browser is not supported. Please use Chrome or Brave for the best experience.',
    errorType: ErrorType.Client
  },
  [ErrorCode.MobileNotSupported]: {
    severity: ErrorSeverity.Critical,
    shouldDisplay: true,
    retryable: false,
    message: 'This video chat application requires a desktop or laptop computer for the best experience.',
    errorType: ErrorType.Client
  },
  [ErrorCode.MultipleSessionsDetected]: {
    severity: ErrorSeverity.Warning,
    shouldDisplay: true,
    retryable: false,
    message: 'Video chat is already active in another tab or window.',
    errorType: ErrorType.Client
  },
  [ErrorCode.Unknown]: {
    severity: ErrorSeverity.Warning,
    shouldDisplay: true,
    retryable: false,
    errorType: ErrorType.Unknown
  }
};

/**
 * Helper to get error configuration
 */
export function getErrorConfig(code: ErrorCode): ErrorConfig {
  return ERROR_CONFIG[code] || ERROR_CONFIG[ErrorCode.Unknown];
}

/**
 * Helper to check if error is critical
 */
export function isCriticalError(code: ErrorCode): boolean {
  return getErrorConfig(code).severity === ErrorSeverity.Critical;
}

/**
 * Helper to check if error is retryable
 */
export function isRetryableError(code: ErrorCode): boolean {
  return getErrorConfig(code).retryable;
}
